import { createContext, useContext, useState, type ReactNode } from 'react';
import { IconButton, PencilIcon } from './Icons';

/**
 * Form primitives.
 *
 * Every control here is ours rather than the browser's. Native date, colour and
 * time inputs cannot be styled, behave differently on each platform, and open
 * chrome we do not control; a checkbox reads as an afterthought next to a real
 * switch. Text and number inputs are plain inputs because there is nothing to
 * gain by reimplementing a caret.
 */

/**
 * View mode versus edit mode.
 *
 * The system being replaced opens a detail panel read-only and enters editing
 * deliberately, through an Edit action. That is worth copying exactly: a screen
 * full of live inputs invites a dispatcher to change a working vehicle when
 * they only meant to read what it does.
 *
 * The mode rides a context rather than a prop on every control, so a panel
 * wraps its body once and each field renders itself the right way. The default
 * is `true`, so a control outside any scope is editable as it always was.
 */
const EditingContext = createContext(true);

export function EditScope({ editing, children }: { editing: boolean; children: ReactNode }) {
  return <EditingContext.Provider value={editing}>{children}</EditingContext.Provider>;
}

export function useEditing(): boolean {
  return useContext(EditingContext);
}

/** How a value reads when the panel is not in edit mode. */
export function StaticValue({ children, mono }: { children?: ReactNode; mono?: boolean }) {
  const empty = children === null || children === undefined || children === '';
  return (
    <span
      className={mono ? 'field-static mono' : 'field-static'}
      data-empty={empty ? 'true' : undefined}
    >
      {empty ? '—' : children}
    </span>
  );
}

export function Field({
  label,
  hint,
  children,
  wide,
}: {
  label: string;
  hint?: string;
  children: ReactNode;
  wide?: boolean;
}) {
  return (
    <label className="field-row" data-wide={wide ? 'true' : undefined}>
      <span className="field-label">
        {label}
        {hint ? <small>{hint}</small> : null}
      </span>
      <span className="field-control">{children}</span>
    </label>
  );
}

export function TextInput({
  value,
  onChange,
  placeholder,
  disabled,
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
}) {
  const editing = useEditing();
  if (!editing) return <StaticValue>{value}</StaticValue>;
  return (
    <input
      type="text"
      className="field"
      value={value}
      placeholder={placeholder}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value)}
    />
  );
}

export function TextArea({
  value,
  onChange,
  rows = 4,
  placeholder,
}: {
  value: string;
  onChange: (value: string) => void;
  rows?: number;
  placeholder?: string;
}) {
  const editing = useEditing();
  if (!editing) {
    return (
      <span className="field-static field-static-block" data-empty={value ? undefined : 'true'}>
        {value || '—'}
      </span>
    );
  }
  return (
    <textarea
      className="field field-area"
      rows={rows}
      value={value}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
    />
  );
}

export function NumberInput({
  value,
  onChange,
  min,
  max,
  step = 1,
  suffix,
}: {
  value: number;
  onChange: (value: number) => void;
  min?: number;
  max?: number;
  step?: number;
  suffix?: string;
}) {
  const editing = useEditing();
  if (!editing) {
    const text = Number.isFinite(value) ? `${value}${suffix ? ` ${suffix}` : ''}` : '';
    return <StaticValue mono>{text}</StaticValue>;
  }
  return (
    <span className="number-wrap">
      <input
        type="number"
        className="field"
        value={Number.isFinite(value) ? value : ''}
        min={min}
        max={max}
        step={step}
        onChange={(e) => {
          const next = Number(e.target.value);
          if (Number.isFinite(next)) onChange(next);
        }}
      />
      {suffix ? <span className="number-suffix">{suffix}</span> : null}
    </span>
  );
}

/** Seconds since midnight as HH:MM, for either mode. */
export function hhmmOf(seconds: number | null): string {
  if (seconds === null || !Number.isFinite(seconds)) return '';
  const h = String(Math.floor(seconds / 3600)).padStart(2, '0');
  const m = String(Math.floor((seconds % 3600) / 60)).padStart(2, '0');
  return `${h}:${m}`;
}

/**
 * Clock time as a text field over seconds-since-midnight.
 *
 * Deliberately not `<input type="time">`: its picker is browser chrome, its
 * 12/24-hour behaviour follows the OS locale rather than the territory, and on
 * some platforms it silently rewrites what was typed.
 */
export function TimeInput({
  seconds,
  onChange,
}: {
  seconds: number | null;
  onChange: (seconds: number | null) => void;
}) {
  const editing = useEditing();
  /*
   * What the field is showing while it is being typed in.
   *
   * Null means "show the committed value". The field used to be driven
   * entirely by `seconds`, and only told the form about a change once the text
   * already parsed -- so every keystroke that did not happen to be a complete
   * time was dropped and React put the old value straight back. Selecting
   * `07:30` and typing `0` reverted to `07:30`; so did pressing Backspace.
   * The field was read-only in practice and gave no sign of it.
   *
   * Holding the draft here is what lets a half-typed time exist long enough to
   * become a whole one.
   */
  const [draft, setDraft] = useState<string | null>(null);
  const committed = hhmmOf(seconds);

  if (!editing) return <StaticValue mono>{committed}</StaticValue>;

  return (
    <input
      type="text"
      className="field field-time"
      value={draft ?? committed}
      placeholder="HH:MM"
      inputMode="numeric"
      onChange={(e) => {
        const raw = e.target.value;
        setDraft(raw);

        const trimmed = raw.trim();
        if (trimmed === '') {
          onChange(null);
          return;
        }
        /* Commit whenever it reads as a time; keep typing when it does not. */
        const m = /^(\d{1,2}):(\d{2})$/.exec(trimmed);
        if (!m) return;
        const h = Number(m[1]);
        const min = Number(m[2]);
        if (h > 23 || min > 59) return;
        onChange(h * 3600 + min * 60);
      }}
      /*
       * Leaving the field drops the draft, so the text goes back to the
       * canonical `HH:MM` of whatever was actually committed -- `7:5` typed
       * and abandoned shows the old time again rather than sitting there
       * looking like it was saved.
       */
      onBlur={() => setDraft(null)}
    />
  );
}

export function Toggle({
  checked,
  onChange,
  label,
  tone,
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label?: string;
  /**
   * Colour the label by what the setting means, for a switch whose two states
   * are not equally harmless.
   *
   * Used sparingly. Most settings are a preference and read better plain; this
   * is for the ones where a dispatcher scanning a form needs to see which way
   * it is set without reading the words -- taking a vehicle out of planning
   * being the case it was added for.
   */
  tone?: 'ok' | 'bad';
}) {
  const editing = useEditing();
  const text = label ?? (checked ? 'Yes' : 'No');
  if (!editing) {
    return (
      <StaticValue>
        <span className="toggle-label" data-tone={tone}>
          {text}
        </span>
      </StaticValue>
    );
  }
  return (
    <button
      type="button"
      className="toggle"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      onClick={() => onChange(!checked)}
    >
      <span className="toggle-track">
        <span className="toggle-knob" />
      </span>
      {label ? (
        <span className="toggle-label" data-tone={tone}>
          {label}
        </span>
      ) : null}
    </button>
  );
}

/**
 * A settings group with a heading and its actions.
 *
 * Three shapes, one component so the pages stay consistent: always-editable
 * (pass `onSave` alone, as the territory-wide settings do), view-then-edit
 * (pass `editing` and `onEdit`), and read-only (pass neither). `actions` holds
 * whatever sits beside the mode buttons, such as New exception on a vehicle.
 */
export function SettingsCard({
  title,
  description,
  children,
  onSave,
  saving,
  dirty,
  editing,
  onEdit,
  onCancel,
  actions,
  saveAtFoot,
}: {
  title: string;
  description?: ReactNode;
  children: ReactNode;
  onSave?: () => void;
  saving?: boolean;
  dirty?: boolean;
  editing?: boolean;
  onEdit?: () => void;
  onCancel?: () => void;
  actions?: ReactNode;
  /**
   * Repeat Cancel and Save below the fields as well as above them.
   *
   * Opt-in, because most panels here are short enough that the header buttons
   * are never off screen. On a long form they are: an order has four sections
   * and a disclosure, and someone who has just finished typing in the last of
   * them should not have to scroll back up to keep the work.
   */
  saveAtFoot?: boolean;
}) {
  const viewing = editing === false;

  /* The same pair in both places, so they cannot drift apart. */
  const editActions = (
    <>
      {onCancel ? (
        <button type="button" className="btn" onClick={onCancel} disabled={saving}>
          Cancel
        </button>
      ) : null}
      {onSave ? (
        <button
          type="button"
          className="btn btn-primary"
          onClick={onSave}
          disabled={saving || dirty === false}
        >
          {saving ? <span className="spinner" /> : null}
          {dirty === false ? 'Saved' : 'Save changes'}
        </button>
      ) : null}
    </>
  );

  return (
    <section className="panel">
      <div className="panel-head">
        <div>
          <h2>{title}</h2>
          {description ? <p className="panel-sub">{description}</p> : null}
        </div>
        <div style={{ flex: 1 }} />
        {actions && viewing ? <div className="panel-actions">{actions}</div> : null}
        {viewing ? (
          onEdit ? (
            /* Icon only, to sit level with the delete and copy actions beside it. */
            <IconButton label="Edit" onClick={onEdit}>
              <PencilIcon />
            </IconButton>
          ) : null
        ) : (
          editActions
        )}
      </div>
      <div className="panel-body">
        <EditScope editing={!viewing}>{children}</EditScope>
        {saveAtFoot && !viewing ? <div className="panel-foot">{editActions}</div> : null}
      </div>
    </section>
  );
}

/**
 * Whether a screen advertises that it has not been seen on the incumbent.
 *
 * Turned off at the operator's request: the warnings had done their job during
 * the build and were noise on a console being shown to other people. The record
 * itself is unchanged -- `NotCaptured` still marks the screens, the `captured`
 * flags in SetupNav are still accurate, and docs/STATE.md lists what is
 * outstanding. Flip this back to true when those screens are reviewed, or
 * whenever the warnings are wanted again.
 */
export const SHOW_NOT_CAPTURED = false;

/**
 * Marks a screen whose real shape we have not seen yet.
 *
 * Being explicit was the point: a plausible-looking form that quietly does not
 * match the system it is replacing is worse than an empty one, because nobody
 * knows to check it. The call sites stay, so the knowledge is still in the code
 * rather than only in someone's head; only the rendering is suppressed.
 */
export function NotCaptured({ what }: { what: string }) {
  if (!SHOW_NOT_CAPTURED) return null;
  return (
    <div className="banner" data-tone="warn">
      <div>
        <b>Not yet captured from the incumbent</b>
        <p>
          {what} is modelled from first principles, not from observing the live system. The
          storage and endpoints work, but expect the field set to change once that screen has
          been reviewed. Do not treat this as settled.
        </p>
      </div>
    </div>
  );
}

/**
 * A collapsed section for the details most people never need.
 *
 * Coordinates live here: a dispatcher edits an address, and the system routes
 * on the position derived from it. Being able to open the drawer and nudge a
 * pin matters when a geocoder puts a rural address in the wrong field, so the
 * numbers are reachable without being in the way.
 */
export function Disclosure({
  label,
  children,
  defaultOpen = false,
}: {
  label: string;
  children: ReactNode;
  defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="disclosure">
      <button
        type="button"
        className="disclosure-head"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        <svg width="10" height="10" viewBox="0 0 10 10" aria-hidden="true">
          <path
            d={open ? 'M2 3.5l3 3 3-3' : 'M3.5 2l3 3-3 3'}
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        {label}
      </button>
      {open ? <div className="disclosure-body">{children}</div> : null}
    </div>
  );
}
